/**
 * Database Operation Queue Service
 *
 * This service serializes all database operations to prevent race conditions
 * and IndexedDB corruption that occurs when multiple jobs try to write
 * to the database simultaneously.
 */

import { isFeatureEnabled } from '../config/featureFlags';
import { db } from '../db';
import databaseHealthMonitor from './DatabaseHealthMonitor';

class DatabaseQueue {
    constructor() {
        this.queue = [];
        this.isProcessing = false;
        this.stats = {
            totalOperations: 0,
            successfulOperations: 0,
            failedOperations: 0,
            averageOperationTime: 0,
            queueHighWaterMark: 0
        };
        this.healthMonitor = databaseHealthMonitor; // Connect to health monitor

        // Set up the connection in health monitor
        databaseHealthMonitor.setDatabaseQueue && databaseHealthMonitor.setDatabaseQueue(this);

        console.log('[DatabaseQueue] Initialized (inactive until feature flag enabled)');
    }

    /**
     * Add a database operation to the queue
     * @param {Function} operation - Async function that performs the database operation
     * @param {string} description - Description of the operation for logging
     * @param {number} priority - Priority level (lower number = higher priority)
     * @returns {Promise} Promise that resolves with the operation result
     */
    async enqueue(operation, description = 'Database operation', priority = 5) {
        if (!isFeatureEnabled('USE_DATABASE_QUEUE')) {
            // If feature is disabled, execute operation directly
            return await operation();
        }

        return new Promise((resolve, reject) => {
            const queueItem = {
                operation,
                description,
                priority,
                resolve,
                reject,
                timestamp: Date.now(),
                id: this.generateOperationId()
            };

            // Insert in priority order (lower priority number = higher priority)
            const insertIndex = this.queue.findIndex(item => item.priority > priority);
            if (insertIndex === -1) {
                this.queue.push(queueItem);
            } else {
                this.queue.splice(insertIndex, 0, queueItem);
            }

            // Update high water mark
            if (this.queue.length > this.stats.queueHighWaterMark) {
                this.stats.queueHighWaterMark = this.queue.length;
            }

            if (isFeatureEnabled('ENABLE_DEBUG_LOGGING')) {
                console.log(`[DatabaseQueue] Enqueued: ${description} (queue size: ${this.queue.length})`);
            }

            // Start processing if not already running
            this.processQueue();
        });
    }

    /**
     * Process the queue of database operations
     */
    async processQueue() {
        if (!isFeatureEnabled('USE_DATABASE_QUEUE')) {
            return;
        }

        if (this.isProcessing || this.queue.length === 0) {
            return;
        }

        this.isProcessing = true;

        while (this.queue.length > 0) {
            const item = this.queue.shift();
            const startTime = Date.now();

            try {
                // Check database health before operation (if health monitor is available)
                if (this.healthMonitor && isFeatureEnabled('USE_HEALTH_MONITOR')) {
                    await this.healthMonitor.checkHealth();
                }

                if (isFeatureEnabled('ENABLE_DEBUG_LOGGING')) {
                    console.log(`[DatabaseQueue] Processing: ${item.description}`);
                }

                // Execute the operation within a transaction wrapper
                const result = await this.executeWithTransaction(item.operation, item.description);

                // Update statistics
                const operationTime = Date.now() - startTime;
                this.updateStats(true, operationTime);

                item.resolve(result);

                if (isFeatureEnabled('ENABLE_DEBUG_LOGGING')) {
                    console.log(`[DatabaseQueue] Completed: ${item.description} (${operationTime}ms)`);
                }

            } catch (error) {
                console.error(`[DatabaseQueue] Failed: ${item.description}`, error);

                // Update statistics
                const operationTime = Date.now() - startTime;
                this.updateStats(false, operationTime);

                // Handle database errors
                await this.handleDatabaseError(error, item);

                item.reject(error);
            }

            // Small delay between operations to prevent overwhelming the database
            if (this.queue.length > 0) {
                await this.delay(10);
            }
        }

        this.isProcessing = false;
    }

    /**
     * Execute a database operation with transaction wrapping
     * @param {Function} operation - The database operation to execute
     * @param {string} description - Description for logging
     * @returns {Promise} Operation result
     */
    async executeWithTransaction(operation, description) {
        try {
            // For operations that don't specify their own transaction,
            // we'll execute them directly and let them handle their own transactions
            return await operation();
        } catch (error) {
            // If the operation failed, check if it's a transaction-related error
            if (this.isTransactionError(error)) {
                console.warn(`[DatabaseQueue] Transaction error in ${description}, retrying...`);
                // Wait a bit and retry once
                await this.delay(100);
                return await operation();
            }
            throw error;
        }
    }

    /**
     * Check if an error is transaction-related
     * @param {Error} error - The error to check
     * @returns {boolean} True if it's a transaction error
     */
    isTransactionError(error) {
        const transactionErrorPatterns = [
            'InvalidStateError',
            'TransactionInactiveError',
            'AbortError',
            'ConstraintError'
        ];

        return transactionErrorPatterns.some(pattern =>
            error.name === pattern || error.message.includes(pattern)
        );
    }

    /**
     * Handle database errors with potential recovery
     * @param {Error} error - The error that occurred
     * @param {object} item - The queue item that failed
     */
    async handleDatabaseError(error, item) {
        if (this.healthMonitor && isFeatureEnabled('USE_HEALTH_MONITOR')) {
            try {
                await this.healthMonitor.handleDatabaseError(error);
            } catch (recoveryError) {
                console.error('[DatabaseQueue] Error recovery failed:', recoveryError);
            }
        }

        // Log error details for debugging
        console.error('[DatabaseQueue] Database operation failed:', {
            operation: item.description,
            error: error.message,
            timestamp: new Date().toISOString(),
            queueSize: this.queue.length
        });
    }

    /**
     * Update operation statistics
     * @param {boolean} success - Whether the operation succeeded
     * @param {number} operationTime - Time taken for the operation
     */
    updateStats(success, operationTime) {
        this.stats.totalOperations++;

        if (success) {
            this.stats.successfulOperations++;
        } else {
            this.stats.failedOperations++;
        }

        // Update average operation time using exponential moving average
        const alpha = 0.1; // Smoothing factor
        this.stats.averageOperationTime =
            (alpha * operationTime) + ((1 - alpha) * this.stats.averageOperationTime);
    }

    /**
     * Generate a unique ID for operations
     * @returns {string} Unique operation ID
     */
    generateOperationId() {
        return `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Add a delay between operations
     * @param {number} ms - Milliseconds to delay
     * @returns {Promise} Promise that resolves after the delay
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Get the current queue size
     * @returns {number} Number of operations in queue
     */
    getQueueSize() {
        return this.queue.length;
    }

    /**
     * Get queue statistics
     * @returns {object} Statistics about queue operations
     */
    getStats() {
        return {
            ...this.stats,
            currentQueueSize: this.queue.length,
            isProcessing: this.isProcessing,
            successRate: this.stats.totalOperations > 0
                ? (this.stats.successfulOperations / this.stats.totalOperations) * 100
                : 0
        };
    }

    /**
     * Clear all pending operations (emergency use only)
     */
    clearQueue() {
        const clearedCount = this.queue.length;

        // Reject all pending operations
        this.queue.forEach(item => {
            item.reject(new Error('Queue cleared'));
        });

        this.queue = [];
        console.warn(`[DatabaseQueue] Cleared ${clearedCount} pending operations`);
    }

    /**
     * Set the health monitor reference
     * @param {object} healthMonitor - The health monitor instance
     */
    setHealthMonitor(healthMonitor) {
        this.healthMonitor = healthMonitor;
        console.log('[DatabaseQueue] Health monitor connected');
    }

    /**
     * Shutdown the queue and reject all pending operations
     */
    shutdown() {
        this.clearQueue();
        this.isProcessing = false;
        console.log('[DatabaseQueue] Shutdown complete');
    }
}

// Convenience wrapper functions for common database operations
export class DatabaseOperations {
    constructor(queue) {
        this.queue = queue;
    }

    /**
     * Add an image to the database
     * @param {object} imageData - Image data to add
     * @returns {Promise} Promise resolving to the new image ID
     */
    async addImage(imageData) {
        return this.queue.enqueue(
            async () => await db.images.add(imageData),
            `Add image: ${imageData.hash || 'unknown'}`,
            3 // Medium priority
        );
    }

    /**
     * Update an image in the database
     * @param {number} id - Image ID to update
     * @param {object} updates - Updates to apply
     * @returns {Promise} Promise resolving to update result
     */
    async updateImage(id, updates) {
        return this.queue.enqueue(
            async () => await db.images.update(id, updates),
            `Update image: ${id}`,
            2 // High priority
        );
    }

    /**
     * Add multiple images in a batch
     * @param {Array} imageDataArray - Array of image data objects
     * @returns {Promise} Promise resolving to array of new IDs
     */
    async addImageBatch(imageDataArray) {
        return this.queue.enqueue(
            async () => {
                // Use a transaction for batch operations
                return await db.transaction('rw', db.images, async () => {
                    return await Promise.all(
                        imageDataArray.map(data => db.images.add(data))
                    );
                });
            },
            `Batch add ${imageDataArray.length} images`,
            1 // Very high priority for batch operations
        );
    }

    /**
     * Add a collection entry
     * @param {object} collectionData - Collection data to add
     * @returns {Promise} Promise resolving to the new collection ID
     */
    async addCollection(collectionData) {
        return this.queue.enqueue(
            async () => await db.collection.add(collectionData),
            `Add collection: ${collectionData.uid}`,
            4 // Lower priority
        );
    }

    /**
     * Update settings
     * @param {string} key - Setting key
     * @param {any} value - Setting value
     * @returns {Promise} Promise resolving to update result
     */
    async updateSetting(key, value) {
        return this.queue.enqueue(
            async () => await db.settings.put({ key, value }),
            `Update setting: ${key}`,
            5 // Lowest priority
        );
    }
}

// Create singleton instances
const databaseQueue = new DatabaseQueue();
const databaseOperations = new DatabaseOperations(databaseQueue);

export { databaseQueue, databaseOperations };
export default databaseQueue;